<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router/composables'
import { welfareConfig as rocnovelConfig } from './configs/rocnovel'
import { welfareConfig as ocereadConfig } from './configs/oceread'
import { welfareConfig as talewaveConfig } from './configs/talewave'
import { welfareConfig as ficmoonConfig } from './configs/ficmoon'
import { welfareConfig as novelarcConfig } from './configs/novelarc'
import { welfareConfig as storyellaConfig } from './configs/storyella'
import { welfareConfig as aurenovelConfig } from './configs/aurenovel'
import { welfareConfig as florastoryConfig } from './configs/florastory'
import { welfareConfig as novelsprigConfig } from './configs/novelsprig'
import { welfareConfig as silktaleConfig } from './configs/silktale'
import { welfareConfig as pearlaConfig } from './configs/pearla'
import { welfareConfig as freeficConfig } from './configs/freefic'
import { welfareConfig as fictoraConfig } from './configs/fictora'
import { welfareConfig as marinovelConfig } from './configs/marinovel'
import { welfareConfig as flipnovelConfig } from './configs/flipnovel'
import { welfareConfig as moonpageConfig } from './configs/moonpage'
import { welfareConfig as storlynConfig } from './configs/storlyn'
import { welfareConfig as novarisConfig } from './configs/novaris'
import { welfareConfig as noveluneConfig } from './configs/novelune'
import { welfareConfig as novelynConfig } from './configs/novelyn'
import { welfareConfig as novelmelloConfig } from './configs/novelmello'
import WelfareDefault from './components/Welfare.default.vue'
import WelfareNovelarc from './components/Welfare.novelarc.vue'
import WelfareNovelyn from './components/Welfare.novelyn.vue'
import WelfareAurenovel from './components/Welfare.aurenovel.vue'
import WelfareFlipnovel from './components/Welfare.flipnovel.vue'
import WelfareNovelmello from './components/Welfare.novelmello.vue'
import { api } from '@/api'
import { useLoadingStore, useToastStore, useUserStore } from '@/stores'
import { isAppInstalled, isChrome, isEdge, isInThirdAppBrowser, isSafari, isTiktok } from '@/utils/bowserEnv'
import { isAndroid, isIOS } from '@/constants'
import { TASK_TYPE } from '@/utils/enum'
import { tripartiteLoginPage } from '@/composables/useTripartiteLogin'
import { useElementClick } from '@/composables/tracks/useElementClick'
import { pageViewTrack } from '@/composables/tracks'
import { loadBrandComponent, loadBrandConfig } from '@/utils/loadBrandConfig'
import { brand } from '@/config/brands'

// 加载当前品牌配置（构建时 tree-shaking）
const config = loadBrandConfig({
  rocnovel: rocnovelConfig,
  oceread: ocereadConfig,
  talewave: talewaveConfig,
  ficmoon: ficmoonConfig,
  novelarc: novelarcConfig,
  storyella: storyellaConfig,
  aurenovel: aurenovelConfig,
  florastory: florastoryConfig,
  silktale: silktaleConfig,
  pearla: pearlaConfig,
  freefic: freeficConfig,
  fictora: fictoraConfig,
  marinovel: marinovelConfig,
  flipnovel: flipnovelConfig,
  moonpage: moonpageConfig,
  novelune: noveluneConfig,
  storlyn: storlynConfig,
  novaris: novarisConfig,
  novelyn: novelynConfig,
  novelmello: novelmelloConfig,
  novelsprig: novelsprigConfig,
})

// 加载当前品牌组件（rocnovel、oceread、ficmoon 共用 default，novelarc 用专属组件）
const WelfarePage = loadBrandComponent({
  rocnovel: WelfareDefault,
  oceread: WelfareDefault,
  ficmoon: WelfareDefault,
  novelarc: WelfareNovelarc,
  storyella: WelfareNovelarc,
  aurenovel: WelfareAurenovel,
  florastory: WelfareNovelarc,
  silktale: WelfareNovelarc,
  pearla: WelfareNovelarc,
  fictora: WelfareDefault,
  marinovel: WelfareDefault,
  flipnovel: WelfareFlipnovel,
  moonpage: WelfareFlipnovel,
  novelune: WelfareFlipnovel,
  storlyn: WelfareFlipnovel,
  novaris: WelfareFlipnovel,
  novelyn: WelfareNovelyn,
  novelmello: WelfareNovelmello,
  novelsprig: WelfareDefault,
})

const { $toast } = useToastStore()
const { $showLoading, $hideLoading } = useLoadingStore()
const { elementClick } = useElementClick()
const userStore = useUserStore()
const route = useRoute()
const router = useRouter()

// 定义背景图 URL 供 CSS v-bind 使用
const homeBgUrl = config.resources.homeBg ? `url(${config.resources.homeBg})` : 'none'
const btnBgColor = config.theme.btnBg

const signed = ref(false)
const progress = ref<any>([])
const modalSigned = ref(false)
const signedNoticePopup = ref(false)
const progressInfo = ref<any>()
const tomorrowInfo = ref<any>()
const checkInDayNum = ref(0)
const showTabbar = computed(() => route.query.from !== 'reward')

const nextCheckInSeconds = ref(0)
let countdownTimer: ReturnType<typeof setInterval> | undefined

function updateNextCheckInCountdown() {
  const now = new Date()
  const nextUtcMidnight = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1)
  nextCheckInSeconds.value = Math.max(0, Math.ceil((nextUtcMidnight - Date.now()) / 1000))
}

const nextCheckInCountdown = computed(() => {
  const hours = Math.floor(nextCheckInSeconds.value / 3600)
  const minutes = Math.floor((nextCheckInSeconds.value % 3600) / 60)
  const seconds = nextCheckInSeconds.value % 60
  return [hours, minutes, seconds].map(value => String(value).padStart(2, '0')).join(':')
})

async function fetchProgress() {
  try {
    const { data } = await api.get<any>('/member/task/signTaskInfo')
    signed.value = data.isSign
    progress.value = data.memberSignResponseList

    // 当前签到信息
    if (signed.value) {
      for (let i = progress.value.length - 1; i >= 0; i--) {
        if (progress.value[i].isSign) {
          progressInfo.value = progress.value[i]
          tomorrowInfo.value = progress.value[i + 1] ?? null
          break
        }
      }
    }
    else {
      for (let i = 0; i < progress.value.length; i++) {
        if (!progress.value[i].isSign) {
          progressInfo.value = progress.value[i]
          tomorrowInfo.value = progress.value[i + 1] ?? null
          break
        }
      }
    }

    // 领取天数
    for (let i = progress.value.length - 1; i >= 0; i--) {
      if (progress.value[i].isSign) {
        checkInDayNum.value = i + 1
        break
      }
    }
    await userStore.init({ force: true })
  }
  catch (error: any) {
    console.error(error)
    $toast(error.msg || 'get check in progress failed')
  }
}

async function onCheckIn() {
  if (signed.value)
    return

  try {
    $showLoading()
    await api.get<any>('/member/task/sign')
    modalSigned.value = true
    pageViewTrack('check_in')
    fetchProgress()
  }
  catch (error: any) {
    console.error(error)
    $toast(error.msg || 'check in failed')
  }
  finally {
    $hideLoading()
    elementClick('check_in')
  }
}

// #region tasks
type ITask = TTaskList & {
  type: string
  status: string
}
const tasks = ref<ITask[]>([])
const showGuidePopupAddHome = ref(false)
const showGuideOpen = ref(false)

const finishPopup = ref(false)
const coins = ref(0)
const completeTaskDescription = ref('')

const showAddTasks = computed(() => {
  const ios = isIOS && (isChrome() || isSafari() || isEdge())
  const android = isAndroid && isChrome()
  return (ios || android) && !isTiktok()
})

async function fatchTasks() {
  try {
    const { data } = await api.post<null, PostTaskListResponse, PostTaskListBody>('/task/list', {})

    // 任务列表
    tasks.value = (data || []).filter(item => (
      item.taskType === 0
      || item.taskType === 1
      || (item.taskType === 2 && showAddTasks.value)
    )) as ITask[]
  }
  catch (error: any) {
    console.error(error)
    $toast(error.msg)
  }
}

const getBtnStatus = function (item: ITask) {
  if (item.taskType === TASK_TYPE.BIND || item.taskType === TASK_TYPE.ADD_BROWSER) {
    if (item.buttonState === 2)
      return config.texts.ok
    else
      return config.texts.go
  }

  if (item.taskType === TASK_TYPE.ADD_TO_HOME_SCREEN) {
    if (isAppInstalled())
      return config.texts.ok
    else
      return config.texts.go
  }
  return ''
}

function finishTaskTrack(taskType: number) {
  if (taskType === TASK_TYPE.BIND)
    pageViewTrack('task_success_login')

  else if (taskType === TASK_TYPE.ADD_BROWSER)
    pageViewTrack('task_success_browser')

  else if (taskType === TASK_TYPE.ADD_TO_HOME_SCREEN)
    pageViewTrack('task_success_homescreen')
}

async function getCoins(item: ITask) {
  try {
    const { code } = await api.post<null, PostTaskCoinsResponse, PostTaskCoinsBody>('/task/coins', {
      taskConfigDetailId: item.id,
      taskType: item.taskType,
      coins: item.coins,
    })
    if (code === 0) {
      finishTaskTrack(item.taskType)
      finishPopup.value = true
      coins.value = item.coins
      fatchTasks()
      userStore.init({ force: true })
    }
  }
  catch (error: any) {
    console.error(error)
    $toast(error.msg)
  }
}
function handleTask(item: ITask) {
  elementClick(getBtnStatus(item).toUpperCase())

  // 领取
  if (item.buttonState === 2 || (item.taskType === TASK_TYPE.ADD_TO_HOME_SCREEN && isAppInstalled())) {
    getCoins(item)
    completeTaskDescription.value = item.taskTitle
    return
  }

  if (item.taskType === TASK_TYPE.BIND) {
    tripartiteLoginPage('Rewards')
    return
  }

  if (item.taskType === TASK_TYPE.ADD_TO_HOME_SCREEN) {
    showGuidePopupAddHome.value = true
    pageViewTrack('task_go_homescreen')
  }

  if (item.taskType === TASK_TYPE.ADD_BROWSER) {
    showGuideOpen.value = true
    pageViewTrack('task_go_browser')
  }
}

function onCheckInOk() {
  modalSigned.value = false
  if (isInThirdAppBrowser())
    showGuideOpen.value = true
}

function onSignedCheckIn() {
  if (signed.value)
    signedNoticePopup.value = true
}

function onBrowseGenres() {
  signedNoticePopup.value = false
  router.push({ name: 'tags' })
}
// #endregion

onMounted(async () => {
  if (brand === 'moonpage') {
    updateNextCheckInCountdown()
    countdownTimer = setInterval(updateNextCheckInCountdown, 1000)
  }
  fetchProgress()
  fatchTasks()
})

onUnmounted(() => {
  if (countdownTimer)
    clearInterval(countdownTimer)
})
</script>

<template>
  <WelfarePage
    :config="config"
    :home-bg-url="homeBgUrl"
    :btn-bg-color="btnBgColor"
    :signed="signed"
    :progress="progress"
    :modal-signed="modalSigned"
    :progress-info="progressInfo"
    :tomorrow-info="tomorrowInfo"
    :check-in-day-num="checkInDayNum"
    :next-check-in-countdown="nextCheckInCountdown"
    :signed-notice-popup="signedNoticePopup"
    :show-tabbar="showTabbar"
    :tasks="tasks"
    :show-guide-popup-add-home="showGuidePopupAddHome"
    :show-guide-open="showGuideOpen"
    :finish-popup="finishPopup"
    :coins="coins"
    :complete-task-description="completeTaskDescription"
    :user-coin-balance="userStore.$state.coinBalance"
    :get-btn-status="getBtnStatus"
    :on-check-in="onCheckIn"
    :on-signed-check-in="onSignedCheckIn"
    :on-browse-genres="onBrowseGenres"
    :handle-task="handleTask"
    :on-check-in-ok="onCheckInOk"
    @update:modal-signed="modalSigned = $event"
    @update:signed-notice-popup="signedNoticePopup = $event"
    @update:show-guide-popup-add-home="showGuidePopupAddHome = $event"
    @update:show-guide-open="showGuideOpen = $event"
    @update:finish-popup="finishPopup = $event"
  />
</template>
